import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { ContactUsForm } from "../graphql/graphql";
import { z } from "zod";

function err(
  messages: ContactUsForm["errorMessages"],
  key: "required" | "invalid" | "charLimit",
): string {
  if (!messages || typeof messages !== "object" || Array.isArray(messages)) {
    return "";
  }
  const v = (messages as Record<string, string>)[key];
  return typeof v === "string" ? v : "";
}

export function useContactUsForm(data?: ContactUsForm[]) {
  const form = useForm<Record<string, string>>({
    mode: "onBlur",
    resolver: zodResolver(getContactUsFormSchema(data)),
    defaultValues: getDefaultValues(data),
  });

  useEffect(() => {
    if (data) {
      form.reset(getDefaultValues(data));
    }
  }, [data, form]);

  return form;
}

function getDefaultValues(data?: ContactUsForm[]) {
  if (!data) {
    return {};
  }

  return data.reduce((acc, curr) => {
    return {
      ...acc,
      [curr?.id as string]: curr?.type === "dropdown" ? undefined : "",
    };
  }, {});
}

function getContactUsFormSchema(data?: ContactUsForm[]) {
  const schema = data?.reduce((acc, curr) => {
    if (curr?.type === "dropdown" && curr?.id) {
      return {
        ...acc,
        [curr.id]: z.object(
          {
            label: z.string(),
            value: z.string(),
          },
          { message: err(curr.errorMessages, "required") },
        ),
      };
    }

    switch (curr?.type) {
      case "email": {
        return {
          ...acc,
          [curr.id as string]: z
            .string()
            .nonempty(err(curr.errorMessages, "required"))
            .max(320, err(curr.errorMessages, "charLimit"))
            .email(err(curr.errorMessages, "invalid")),
        };
      }

      case "textarea": {
        return {
          ...acc,
          [curr.id as string]: z
            .string()
            .min(1, err(curr.errorMessages, "required"))
            .max(500, err(curr.errorMessages, "charLimit")),
        };
      }

      case "radio": {
        return {
          ...acc,
          [curr.id as string]: z
            .string()
            .nonempty(err(curr?.errorMessages, "required")),
        };
      }

      case "text": {
        return {
          ...acc,
          [curr.id as string]: z
            .string()
            .min(1, err(curr.errorMessages, "required"))
            .max(500, err(curr.errorMessages, "charLimit")),
        };
      }

      default: {
        return {
          ...acc,
          [curr?.id as string]: z
            .string()
            .nonempty(err(curr?.errorMessages, "required"))
            .regex(new RegExp(/\S/), err(curr?.errorMessages, "required"))
            .regex(
              new RegExp(/^[a-zA-Z ]*$/),
              err(curr?.errorMessages, "invalid"),
            )
            .max(50, err(curr?.errorMessages, "charLimit")),
        };
      }
    }
  }, {});

  return z.object(schema || {});
}
